import React, { memo } from "react"
import PropTypes from "prop-types"
import DOMPurify from "dompurify"
import { Button, Flex } from "antd"
import { EmailTemplateLogo } from "ui/EmailTemplateLogo"
import PreviewMedia from "components/common/PreviewMedia"
import { useSelector } from "react-redux"

import facebookIcon from "assets/icons/new/facebook.svg"
import instagramIcon from "assets/icons/new/instagram.svg"
import twitterIcon from "assets/icons/new/twitter.svg"
import styles from "./TemplatePreview.module.css"

TemplatePreview.propTypes = {
  form: PropTypes.shape({
    template_type: PropTypes.string.isRequired,
    heading: PropTypes.string,
    paragraph_text: PropTypes.string,
    photos: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        _id: PropTypes.string,
        mediaType: PropTypes.string.isRequired,
        file_preview: PropTypes.string,
        url: PropTypes.string
      })
    ),
    button_text: PropTypes.string,
    button_url: PropTypes.string,
    signature: PropTypes.string
  }).isRequired,
  template: PropTypes.shape({
    category: PropTypes.string.isRequired,
    social_media: PropTypes.object,
    custom_logo_url: PropTypes.string
  }).isRequired,
  isEdit: PropTypes.bool.isRequired
}

function TemplatePreview({ form, template, isEdit }) {
  const { metadata } = useSelector((state) => state.templates)

  const withPhoto = ["small_gallery", "large_gallery"].includes(template.category)
  const isCentered = ["small_gallery", "large_gallery"].includes(template.category)
  const alignClass = isCentered ? styles.centered : styles.leftAligned
  const isEmail = form.template_type === "email"
  const isFirstBasic = template.category === "first_basic"
  const socialMedia = isEdit ? template.social_media : metadata.social_media
  const hasPhoto = form.photos?.length > 0

  return (
    <div className={`${isEmail ? styles.container : styles.containerSms}`}>
      <div className={styles.body}>
        {isEmail && <EmailTemplateLogo logoUrl={template.custom_logo_url} height={35} className={styles.logo} />}

        {!isFirstBasic && form.heading && (
          <h3 className={`${styles.heading} ${alignClass}`}>{form.heading}</h3>
        )}

        <p className={styles.greeting}>Hello [Client Name]</p>

        {form.paragraph_text && (
          <div
            className={styles.paragraphText}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(form.paragraph_text) }}
          />
        )}

        {withPhoto && hasPhoto && isEmail && (
          <Flex vertical gap="large" className={styles.photoList}>
            {form.photos?.map((photo) => (
              <div key={photo.id || photo._id} className={styles.photoItem}>
                <PreviewMedia type={photo.mediaType} preview={photo.file_preview || photo.url} ratio="4x3" rounded={false} imageHeight="100%" />
              </div>
            ))}
          </Flex>
        )}

        {!isFirstBasic && isEmail && (form.button_text || form.button_url) && (
          <div className={styles.buttonWrapper}>
            <Button type="primary" size="large">
              {form.button_text || "Browse Trips"}
            </Button>
          </div>
        )}

        <div className={styles.signatureBlock}>
          {!isEmail && form.button_url && (
            <>
              <Button type="link" block>
                {form.button_url}
              </Button>
              <br />
            </>
          )}
          {form.signature && <div>{form.signature}</div>}
          <div className={styles.guideProfileName}>{"[Guide profile name]"}</div>
        </div>
      </div>

      {socialMedia && isEmail && (
        <Flex justify="center" gap="large" className={styles.socialBar}>
          {socialMedia.facebook_url && <img src={facebookIcon} alt="" />}
          {socialMedia.instagram_url && <img src={instagramIcon} alt="" />}
          {socialMedia.twitter_url && <img src={twitterIcon} alt="" />}
        </Flex>
      )}
    </div>
  )
}

export default memo(TemplatePreview)
